"use client"
import { useState } from "react"
import { useGlobalContext } from "../../lib/storage"
import ThemeJson from "../../constants/UIServer.json"

/**
 * DrillDownTable
 *
 * Renders a "drillDown" type report with multi-level grouping and collapse.
 *
 * Levels are read from groupingConfiguration.levels (sorted by .level).
 * Each level produces a collapsible group header row with subtotals.
 * The leaf records show as indented detail rows under the deepest group level.
 *
 * Example (2 levels: branch → sales_executive):
 *   ▼ Abids Gift       |  20.00  |  1,24,763.00   ← level-0 group row (collapsible)
 *     ▼ AKHLESH OJHA   |   2.00  |      400.00    ← level-1 group row (collapsible)
 *         detail row   |   2.00  |      400.00    ← leaf detail row
 */
const DrillDownTable = (props) => {
  const { globalStore } = useGlobalContext()
  const Themes = ThemeJson
  const {
    columnsData,
    gridData,
    fullScreen,
    onDrillDown,
    onNavigation,
    parametersToSend,
    columnGrouping,        // columnGroups from query config
    groupingConfiguration, // groupingConfiguration from query config
    selectedLevel: selectedLevelProp,
    onLevelChange,
  } = props

  const showGrandTotal = groupingConfiguration?.showGrandTotal !== false
  const defaultCollapsed = columnGrouping?.[0]?.isCollapsed === true

  const userPreferences = JSON.parse(localStorage.getItem("userPreferences"))
  const roundOffDecimalValue = userPreferences?.decimalPlaces || 2

  // ── Extract levels from groupingConfiguration, sorted by level number ──
  const levels = (groupingConfiguration?.levels || []).slice().sort((a, b) => a.level - b.level)
  const levelFields = levels.map((l) => l.fieldName) // e.g. ["branch", "sales_executive"]

  // Fallback: if no levels configured, try columnGroups then first string column
  const fallbackGroupField =
    columnGrouping?.[0]?.columns?.[0] ||
    columnsData?.find((c) => c.type !== "Amount" && c.type !== "Numeric")?.dataIndex

  const effectiveLevelFields = levelFields.length > 0 ? levelFields : fallbackGroupField ? [fallbackGroupField] : []

  // ── Level selector: controlled by parent if selectedLevelProp provided ──
  const maxLevel = effectiveLevelFields.length || 1
  const [localLevel, setLocalLevel] = useState(maxLevel)
  const selectedLevel = selectedLevelProp || localLevel
  const setSelectedLevel = (val) => {
    setLocalLevel(val)
    onLevelChange?.(val)
  }
  const activeFields = effectiveLevelFields.slice(0, selectedLevel)
  const isFullDepth = selectedLevel >= effectiveLevelFields.length

  // ── Hide group-field columns beyond the first (they are rendered as tree
  //    nodes in col 0, so their dedicated columns would just be empty) ──────
  const hiddenGroupCols = new Set(activeFields.slice(1))
  const displayColumns = (columnsData || []).filter((c) => !hiddenGroupCols.has(c.dataIndex))

  // ── Numeric fields for summing subtotals ────────────────────────────────
  const numericFields = (columnsData || [])
    .filter((c) => c.type === "Amount" || c.type === "Numeric")
    .map((c) => c.dataIndex)

  // ── Build nested group tree ─────────────────────────────────────────────
  // Returns an array of group objects. Each group has:
  //   { field, key, depth, subtotals, levelConfig, isLeaf, children }
  // children is either another array of groups (isLeaf=false) or raw data rows (isLeaf=true)
  const buildGroupTree = (rows, fields, depth) => {
    if (!fields.length || !rows.length) return rows
    const field = fields[0]
    const rest = fields.slice(1)
    const map = new Map()
    rows.forEach((row) => {
      const k = row[field] != null ? String(row[field]).trim() : ""
      if (!map.has(k)) map.set(k, [])
      map.get(k).push(row)
    })
    const groups = []
    map.forEach((groupRows, key) => {
      const subtotals = {}
      numericFields.forEach((f) => {
        subtotals[f] = groupRows.reduce((s, r) => s + parseFloat(r[f] || 0), 0)
      })
      groups.push({
        field,
        key,
        depth,
        subtotals,
        levelConfig: levels[depth] || null,
        isLeaf: rest.length === 0,
        children: rest.length > 0 ? buildGroupTree(groupRows, rest, depth + 1) : groupRows,
      })
    })
    return groups
  }

  const groupTree = activeFields.length > 0
    ? buildGroupTree(gridData || [], activeFields, 0)
    : null

  // ── Collapsed state ─────────────────────────────────────────────────────
  // Keys are composite paths: "branchVal||salesExecVal" so siblings don't clash
  const [collapsed, setCollapsed] = useState(() => {
    if (!defaultCollapsed || !groupTree) return new Set()
    return new Set(groupTree.map((g) => g.key))
  })

  const toggleCollapse = (compositeKey) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      next.has(compositeKey) ? next.delete(compositeKey) : next.add(compositeKey)
      return next
    })
  }

  // ── Grand totals ────────────────────────────────────────────────────────
  const grandTotals = {}
  numericFields.forEach((f) => {
    grandTotals[f] = (gridData || []).reduce((s, r) => s + parseFloat(r[f] || 0), 0)
  })

  // ── Formatter ───────────────────────────────────────────────────────────
  const fmt = (val, type) => {
    if (type === "Amount" || type === "Numeric") {
      const n = parseFloat(val || 0)
      return n.toLocaleString("en-US", {
        minimumFractionDigits: roundOffDecimalValue,
        maximumFractionDigits: roundOffDecimalValue,
      })
    }
    return val ?? ""
  }

  // ── Background colors per depth ─────────────────────────────────────────
  const depthBg = ["#eef2f7", "#f5f7fa", "#fafbfc"]
  const getGroupBg = (depth) => depthBg[depth] || "#f5f5f5"

  // ── Render a group header row ───────────────────────────────────────────
  // All group labels are shown in column 0 (first column) with depth-based
  // indentation so every level stacks as a tree under the first column.
  const renderGroupRow = (group, compositeKey, isCollapsedState, showToggle) => {
    const showSubtotal = group.levelConfig?.showSubtotal !== false
    const indent = group.depth
    return (
      <tr
        key={`grp-${compositeKey}`}
        className={`dd-group-row dd-group-depth-${group.depth}`}
        style={{ backgroundColor: getGroupBg(group.depth), cursor: showToggle ? "pointer" : "default" }}
        onClick={showToggle ? () => toggleCollapse(compositeKey) : undefined}
      >
        {displayColumns.map((col, ci) => {
          const isNumeric = col.type === "Amount" || col.type === "Numeric"
          const isFirstCol = ci === 0
          return (
            <td key={`grp-${compositeKey}-${col.dataIndex || ci}`}>
              <div
                style={{
                  textAlign: isNumeric ? "right" : "left",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "9px",
                  fontWeight: 700,
                  color: "#000000",
                  paddingLeft: isFirstCol ? `${indent * 20 + 8}px` : "8px",
                  display: isFirstCol ? "flex" : undefined,
                  alignItems: isFirstCol ? "center" : undefined,
                  gap: isFirstCol ? "4px" : undefined,
                  justifyContent: isNumeric ? "flex-end" : "flex-start",
                  whiteSpace: "nowrap",
                }}
              >
                {isFirstCol && showToggle && (
                  <span style={{ fontSize: "9px", lineHeight: 1, color: "#555", flexShrink: 0 }}>
                    {isCollapsedState ? "▶" : "▼"}
                  </span>
                )}
                {isFirstCol
                  ? group.key || "—"
                  : isNumeric && showSubtotal && group.subtotals[col.dataIndex] != null
                    ? fmt(group.subtotals[col.dataIndex], col.type)
                    : ""}
              </div>
            </td>
          )
        })}
      </tr>
    )
  }


  // ── Render deepest-level group as a plain detail-style line ────────────
  // Shows the group key in col 0 and subtotals in numeric cols — no bold header, no children below.
  const renderLeafAsDetailRow = (group, compositeKey) => {
    const showSubtotal = group.levelConfig?.showSubtotal !== false
    const indent = group.depth
    return (
      <tr key={`leaf-${compositeKey}`} className="dd-detail-row">
        {displayColumns.map((col, ci) => {
          const isNumeric = col.type === "Amount" || col.type === "Numeric"
          const isFirstCol = ci === 0
          return (
            <td key={`leaf-${compositeKey}-${col.dataIndex || ci}`}>
              <div
                style={{
                  textAlign: isNumeric ? "right" : "left",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "9px",
                  fontWeight: 400,
                  color: "#000000",
                  paddingLeft: isFirstCol ? `${indent * 20 + 8}px` : "8px",
                  whiteSpace: "nowrap",
                }}
              >
                {isFirstCol
                  ? group.key || "—"
                  : isNumeric && showSubtotal && group.subtotals[col.dataIndex] != null
                    ? fmt(group.subtotals[col.dataIndex], col.type)
                    : ""}
              </div>
            </td>
          )
        })}
      </tr>
    )
  }

  // ── Recursively render group tree into flat row array ───────────────────
  const renderGroups = (groups, parentPath = "") => {
    const rows = []
    groups.forEach((group) => {
      const compositeKey = parentPath ? `${parentPath}||${group.key}` : group.key
      const isCollapsedState = collapsed.has(compositeKey)

      if (!group.isLeaf) {
        // intermediate level: collapsible group header
        rows.push(renderGroupRow(group, compositeKey, isCollapsedState, true))
        if (!isCollapsedState) {
          renderGroups(group.children, compositeKey).forEach((r) => rows.push(r))
        }
      } else if (isFullDepth) {
        // deepest level at full depth: render as plain detail line (no bold header, no children)
        rows.push(renderLeafAsDetailRow(group, compositeKey))
      } else {
        // reduced level view (level dropdown): show group summary row only
        rows.push(renderGroupRow(group, compositeKey, isCollapsedState, false))
      }
    })
    return rows
  }

  // ── Flat fallback when no grouping configured ───────────────────────────
  const renderFlatRows = () =>
    (gridData || []).map((row, i) => (
      <tr key={i} className="dd-detail-row">
        {displayColumns.map((col, ci) => {
          const val = row[col.dataIndex]
          const isNumeric = col.type === "Amount" || col.type === "Numeric"
          return (
            <td key={`${i}-${col.dataIndex || ci}`}>
              <div
                style={{
                  textAlign: isNumeric ? "right" : "left",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "9px",
                  padding: "0 8px",
                }}
              >
                {isNumeric ? fmt(val, col.type) : (val ?? "")}
              </div>
            </td>
          )
        })}
      </tr>
    ))

  return (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        backgroundColor: "#ffffff",
        display: "inline-block",
        width: "auto",
        position: "relative",
      }}
    >
      <style>{`
        .dd-table-container {
          display: inline-block;
          width: auto;
          min-width: fit-content;
          overflow: auto;
          max-height: ${fullScreen ? "70vh" : "66vh"};
        }

        .dd-table {
          border-collapse: collapse;
          font-family: "Inter, sans-serif";
          font-size: 9px;
          width: auto;
          min-width: fit-content;
          border: 1px solid #d9d9d9;
          background-color: #ffffff;
        }

        .dd-table th {
          background-color: #4472c4;
          border: 1px solid #d9d9d9;
          font-weight: 600;
          font-family: "Inter, sans-serif";
          font-size: 9px;
          color: #ffffff;
          padding: 5px 12px;
          white-space: nowrap;
          text-align: left;
        }

        .dd-table td {
          border: 1px solid #d9d9d9;
          padding: 3px 10px;
          font-family: "Inter, sans-serif";
          font-size: 9px;
          color: #000000;
          white-space: nowrap;
        }

        .dd-group-row:hover td {
          filter: brightness(0.95);
          cursor: pointer;
        }

        .dd-detail-row td {
          background-color: #ffffff;
        }

        .dd-detail-row:hover td {
          background-color: #f0f4ff;
        }

        .dd-grand-total-row td {
          background-color: #d6dce4 !important;
          border-top: 2px solid #aaaaaa !important;
          font-weight: 700;
          font-size: 9px;
        }
      `}</style>

      {effectiveLevelFields.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: "11px", fontWeight: 600, color: "#333" }}>
            Grouping Level:
          </span>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(Number(e.target.value))}
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "11px",
              padding: "2px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              outline: "none",
            }}
          >
            {effectiveLevelFields.map((_, i) => (
              <option key={i + 1} value={i + 1}>
                Level {i + 1}{levels[i]?.label ? ` - ${levels[i].label}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="dd-table-container">
        <table className="dd-table">
          <thead>
            <tr>
              {displayColumns.map((col, i) => (
                <th key={col.dataIndex || i}>{col.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupTree ? renderGroups(groupTree) : renderFlatRows()}

            {showGrandTotal && (
              <tr className="dd-grand-total-row">
                {displayColumns.map((col, i) => (
                  <td key={`grand-${col.dataIndex || i}`}>
                    <div
                      style={{
                        textAlign: i === 0 ? "left" : (col.type === "Numeric" || col.type === "Amount") ? "right" : "left",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "9px",
                        fontWeight: 700,
                        color: "#000000",
                        paddingLeft: "8px",
                      }}
                    >
                      {i === 0
                        ? "Grand Total"
                        : (col.type === "Numeric" || col.type === "Amount") && grandTotals[col.dataIndex] != null
                          ? fmt(grandTotals[col.dataIndex], col.type)
                          : ""}
                    </div>
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DrillDownTable
